import * as React from 'react';
import {
    Alert, Box, Button, Card, CardContent, Checkbox, Chip, Grid, ListItemText, MenuItem, Stack, TextField, Tooltip,
    Typography, Link as MuiLink,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { Link as RouterLink } from 'react-router-dom';
import { AI_LABELS, AiUsageCallsFilter, getAiMeta, getAiUsageCalls, getAiUsageSummary } from '../../Api';
import {
    AI_USAGE_GROUP_BYS, AI_USAGE_STATUSES, AiMetaDto, AiUsageCallDto, AiUsageGroupBy, AiUsageRow, AiUsageStatus,
    AiUsageSummaryDto,
} from '../../models/dtos/AiDto';
import {
    aiLoadErrorText, AiUsageStatusChip, aiUtcDay, EnumSelect, formatAiCount, formatAiDate, formatAiMs, formatAiUsd, KpiCard,
} from '../../components/ai/AiShared';
import { CostBarChart, usageRowLabel } from '../../components/ai/AiUsageCharts';
import { TaskExecutionLink } from '../../components/ai/AiTaskExecutionsGrid';

function SummaryRowsGrid({ rows, groupBy }: { rows: AiUsageRow[]; groupBy: AiUsageGroupBy[] }) {
    const groupColumns: GridColDef<AiUsageRow & { id: number }>[] = [];
    if (groupBy.includes('day')) groupColumns.push({ field: 'day', headerName: 'Day', width: 110 });
    if (groupBy.includes('caller')) groupColumns.push({ field: 'caller', headerName: 'Caller', width: 150 });
    if (groupBy.includes('task')) groupColumns.push({ field: 'taskCode', headerName: 'Task', width: 200 });
    if (groupBy.includes('agent')) groupColumns.push({ field: 'agentCode', headerName: 'Agent', width: 180 });
    if (groupBy.includes('model')) groupColumns.push({ field: 'modelKey', headerName: 'Model', width: 170 });
    const columns: GridColDef<AiUsageRow & { id: number }>[] = [
        ...groupColumns,
        { field: 'calls', headerName: 'Calls', width: 90, type: 'number' },
        { field: 'errors', headerName: 'Errors', width: 90, type: 'number' },
        { field: 'blocked', headerName: 'Blocked', width: 90, type: 'number' },
        { field: 'inputTokens', headerName: 'Input tok', width: 110, type: 'number' },
        { field: 'outputTokens', headerName: 'Output tok', width: 110, type: 'number' },
        { field: 'cacheReadTokens', headerName: 'Cache read', width: 110, type: 'number' },
        { field: 'costUsd', headerName: 'Cost', width: 120, type: 'number', valueFormatter: ({ value }) => formatAiUsd(value as number) },
    ];
    return (
        <DataGrid
            autoHeight
            rows={rows.map((r, i) => ({ ...r, id: i }))}
            columns={columns}
            density="compact"
            disableRowSelectionOnClick
            initialState={{ pagination: { paginationModel: { pageSize: 25 } }, sorting: { sortModel: [{ field: groupBy.includes('day') ? 'day' : 'costUsd', sort: 'desc' }] } }}
            pageSizeOptions={[25, 50, 100]}
            localeText={{ noRowsLabel: 'No usage' }}
        />
    );
}

function CallsGrid({ from, to, callers }: { from: string; to: string; callers: string[] }) {
    const [caller, setCaller] = React.useState('');
    const [taskCode, setTaskCode] = React.useState('');
    const [agentCode, setAgentCode] = React.useState('');
    const [status, setStatus] = React.useState<AiUsageStatus | ''>('');
    const [endUserId, setEndUserId] = React.useState('');
    const [filter, setFilter] = React.useState<AiUsageCallsFilter>({});
    const [rows, setRows] = React.useState<AiUsageCallDto[]>([]);
    const [rowCount, setRowCount] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({ page: 0, pageSize: 25 });

    React.useEffect(() => {
        const timer = window.setTimeout(() => setFilter({
            from, to, caller: caller || undefined, taskCode: taskCode.trim() || undefined, agentCode: agentCode.trim() || undefined,
            status: status || undefined, endUserId: endUserId || undefined,
        }), 400);
        return () => window.clearTimeout(timer);
    }, [from, to, caller, taskCode, agentCode, status, endUserId]);

    React.useEffect(() => { setPaginationModel(p => (p.page === 0 ? p : { ...p, page: 0 })); }, [filter]);

    const load = React.useCallback(async () => {
        setLoading(true);
        const page = await getAiUsageCalls(filter, paginationModel.page * paginationModel.pageSize, paginationModel.pageSize);
        setLoading(false);
        if (page == null) {
            setError(aiLoadErrorText(AI_LABELS.usageCalls, 'Failed to load calls.'));
            setRows([]);
            setRowCount(0);
            return;
        }
        setError(null);
        setRows(page.items ?? []);
        setRowCount(page.totalItems ?? 0);
    }, [filter, paginationModel]);
    React.useEffect(() => { load(); }, [load]);

    const columns: GridColDef<AiUsageCallDto>[] = [
        { field: 'createdAt', headerName: 'At', width: 165, sortable: false, valueFormatter: ({ value }) => formatAiDate(value as string) },
        { field: 'caller', headerName: 'Caller', width: 130, sortable: false },
        {
            field: 'target', headerName: 'Task / agent', width: 220, sortable: false,
            renderCell: ({ row }) => (
                <Stack>
                    {row.taskCode && <MuiLink component={RouterLink} to={`/ai/tasks/${encodeURIComponent(row.taskCode)}`} underline="hover" variant="body2">task {row.taskCode}</MuiLink>}
                    {row.agentCode && <MuiLink component={RouterLink} to={`/ai/agents/${encodeURIComponent(row.agentCode)}`} underline="hover" variant="body2">agent {row.agentCode}</MuiLink>}
                    {row.rootAgentCode && row.rootAgentCode !== row.agentCode && <Typography variant="caption" color="text.secondary">root {row.rootAgentCode}</Typography>}
                    {row.runId && <MuiLink component={RouterLink} to={`/ai/runs/${encodeURIComponent(row.runId)}`} underline="hover" variant="caption">run {row.runId.slice(0, 8)}…</MuiLink>}
                    {row.taskExecutionId && <TaskExecutionLink executionId={row.taskExecutionId} label="input & output" />}
                </Stack>
            ),
        },
        { field: 'status', headerName: 'Status', width: 110, sortable: false, renderCell: ({ row }) => <AiUsageStatusChip status={row.status} /> },
        {
            field: 'modelKey', headerName: 'Model', width: 180, sortable: false,
            renderCell: ({ row }) => (
                <Stack>
                    <Typography variant="body2">{row.modelKey ?? '—'}{row.isFallback ? ' (fallback)' : ''}</Typography>
                    {row.providerKeyName && <Typography variant="caption" color="text.secondary">key {row.providerKeyName}</Typography>}
                </Stack>
            ),
        },
        {
            field: 'tokens', headerName: 'Tokens in / out', width: 140, sortable: false,
            renderCell: ({ row }) => (
                <Tooltip title={`cache write ${formatAiCount(row.cacheWriteTokens)} · cache read ${formatAiCount(row.cacheReadTokens)}`}>
                    <Typography variant="body2">{formatAiCount(row.inputTokens)} / {formatAiCount(row.outputTokens)}</Typography>
                </Tooltip>
            ),
        },
        { field: 'costUsd', headerName: 'Cost', width: 110, sortable: false, valueFormatter: ({ value }) => formatAiUsd(value as number) },
        { field: 'latencyMs', headerName: 'Latency', width: 90, sortable: false, valueFormatter: ({ value }) => formatAiMs(value as number | null) },
        {
            field: 'endUser', headerName: 'End user / subject', width: 170, sortable: false,
            renderCell: ({ row }) => (
                <Stack>
                    {row.endUserId != null && <MuiLink component={RouterLink} to={`/users/${row.endUserId}`} underline="hover" variant="body2">#{row.endUserId}</MuiLink>}
                    {row.subjectType && <Typography variant="caption" color="text.secondary">{row.subjectType} / {row.subjectId}</Typography>}
                    {row.endUserId == null && !row.subjectType && '—'}
                </Stack>
            ),
        },
        {
            field: 'error', headerName: 'Error', flex: 1, minWidth: 200, sortable: false,
            renderCell: ({ row }) => row.error ? <Typography variant="caption" color="error" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{row.error}</Typography> : null,
        },
    ];

    return (
        <Stack gap={1.5}>
            <Typography variant="h6">Calls</Typography>
            <Stack direction="row" gap={1.5} flexWrap="wrap">
                <TextField select size="small" label="Caller" value={caller} sx={{ minWidth: 170 }} onChange={e => setCaller(e.target.value)}>
                    <MenuItem value=""><em>Any</em></MenuItem>
                    {callers.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
                <TextField size="small" label="Task code" value={taskCode} onChange={e => setTaskCode(e.target.value)} />
                <TextField size="small" label="Agent code" value={agentCode} onChange={e => setAgentCode(e.target.value)} />
                <EnumSelect size="small" label="Status" value={status} options={AI_USAGE_STATUSES} allowEmpty fullWidth={false} sx={{ minWidth: 150 }} onChange={setStatus} />
                <TextField size="small" label="End user id" value={endUserId} onChange={e => setEndUserId(e.target.value.replace(/\D/g, ''))} />
            </Stack>
            {error && <Alert severity="error">{error}</Alert>}
            <DataGrid
                autoHeight
                rows={rows}
                columns={columns}
                getRowHeight={() => 'auto'}
                paginationMode="server"
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 25, 50, 100]}
                loading={loading}
                disableRowSelectionOnClick
                disableColumnMenu
                sx={{ '& .MuiDataGrid-cell': { py: 0.75 } }}
                localeText={{ noRowsLabel: 'No calls' }}
            />
        </Stack>
    );
}

export default function AiUsagePage() {
    const [meta, setMeta] = React.useState<AiMetaDto | null>(null);
    const [from, setFrom] = React.useState(aiUtcDay(29));
    const [to, setTo] = React.useState(aiUtcDay(0));
    const [groupBy, setGroupBy] = React.useState<AiUsageGroupBy[]>(['task', 'agent']);
    const [summary, setSummary] = React.useState<AiUsageSummaryDto | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => { getAiMeta().then(setMeta); }, []);

    const load = React.useCallback(async () => {
        setLoading(true);
        const s = await getAiUsageSummary(from || null, to || null, groupBy);
        setLoading(false);
        setSummary(s);
        setError(s == null ? aiLoadErrorText(AI_LABELS.usageSummary, 'Failed to load usage summary.') : null);
    }, [from, to, groupBy]);
    React.useEffect(() => { load(); }, [load]);

    const totals = summary?.totals;
    const setPreset = (days: number) => { setFrom(aiUtcDay(days - 1)); setTo(aiUtcDay(0)); };

    return (
        <Stack gap={2.5} sx={{ mx: 5, my: 5 }}>
            <Box>
                <Typography variant="h4">AI usage</Typography>
                <Typography variant="body2" color="text.secondary">Spend and calls by day, caller, task, agent and model (UTC days, `to` inclusive).</Typography>
            </Box>
            <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
                <TextField size="small" type="date" label="From" value={from} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField size="small" type="date" label="To" value={to} onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
                <Stack direction="row" gap={0.5}>
                    {[1, 7, 30, 90].map(d => <Chip key={d} label={d === 1 ? 'Today' : `${d}d`} size="small" onClick={() => setPreset(d)} variant="outlined" />)}
                </Stack>
                <TextField select size="small" label="Group by" sx={{ minWidth: 260 }}
                    SelectProps={{
                        multiple: true,
                        value: groupBy,
                        renderValue: selected => (selected as string[]).join(', ') || 'none',
                        onChange: e => setGroupBy(AI_USAGE_GROUP_BYS.filter(g => (e.target.value as string[]).includes(g))),
                    }}>
                    {AI_USAGE_GROUP_BYS.map(g => (
                        <MenuItem key={g} value={g}>
                            <Checkbox size="small" checked={groupBy.includes(g)} />
                            <ListItemText primary={g} />
                        </MenuItem>
                    ))}
                </TextField>
                <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
            </Stack>
            {error && <Alert severity="error">{error}</Alert>}
            <Grid container spacing={2}>
                <Grid item xs={6} md={2}><KpiCard title="Cost" value={formatAiUsd(totals?.costUsd)} /></Grid>
                <Grid item xs={6} md={2}><KpiCard title="Calls" value={formatAiCount(totals?.calls)} /></Grid>
                <Grid item xs={6} md={2}><KpiCard title="Errors" value={formatAiCount(totals?.errors)} /></Grid>
                <Grid item xs={6} md={2}><KpiCard title="Blocked" value={formatAiCount(totals?.blocked)} /></Grid>
                <Grid item xs={6} md={2}><KpiCard title="Input tokens" value={formatAiCount(totals?.inputTokens)} /></Grid>
                <Grid item xs={6} md={2}><KpiCard title="Output tokens" value={formatAiCount(totals?.outputTokens)} /></Grid>
            </Grid>
            {groupBy.length > 0 && (
                <Grid container spacing={2}>
                    <Grid item xs={12} lg={5}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="subtitle1" fontWeight="bold">Cost by {groupBy.join(' / ')} (top 15)</Typography>
                                <CostBarChart rows={summary?.rows ?? []} label={usageRowLabel} limit={15} />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} lg={7}>
                        <SummaryRowsGrid rows={summary?.rows ?? []} groupBy={summary?.groupBy ?? groupBy} />
                    </Grid>
                </Grid>
            )}
            <CallsGrid from={from} to={to} callers={meta?.callers ?? []} />
        </Stack>
    );
}
